import { RegistryContext, useAtomValue } from '@effect/atom-react';
import { GetListAudioChaptersV2200 } from '@listening/api-contract/schemas';
import { shallowEqualArrays } from '@listening/utils/ts-utils';
import { AsyncResult, Atom } from 'effect/reactivity';
import { Duration, type Effect } from 'effect';
import { use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type ApiServices, ssrApiQuery } from '../query/api-runtime';
import type { LegacyChapterChunkPage } from '../query/chapters';
import {
  enabledLegacyChapterChunks,
  legacyChapterChunkParams,
  type LegacyChapterChunkParams,
} from './chapter-chunks';

export const LEGACY_CHAPTER_CHUNKS_BEHIND = 1;
export const LEGACY_CHAPTER_CHUNKS_AHEAD = 1;
export const LEGACY_CHAPTER_VISIBILITY_DEBOUNCE_MS = 500;

/**
 * How an app loads legacy chapter text, as atoms. A chunk is a word-level tree
 * (hundreds of bytes per word), so a reader keeps every chunk it loaded while
 * it is open and drops them `timeToLive` after it closes: across documents,
 * longer retention is what fills a phone's memory budget. The raw chunk is
 * what the server renders and the page carries, so the web reader's first
 * chunk hydrates; `toPage` shapes it for the reader.
 */
export const makeLegacyChapterChunkReader = <TPage, E>(options: {
  readonly fetchChunk: (
    params: LegacyChapterChunkParams,
  ) => Effect.Effect<LegacyChapterChunkPage, E, ApiServices>;
  readonly toPage: (page: LegacyChapterChunkPage) => TPage;
  readonly timeToLive: Duration.Input;
}) => {
  const chunkSource = Atom.family((params: LegacyChapterChunkParams) =>
    ssrApiQuery(options.fetchChunk(params), {
      key: `legacy-chapter-chunk:${params.audioId}:${params.cursor}:${params.limit.toString()}`,
      success: GetListAudioChaptersV2200,
      staleTime: Duration.infinity,
      timeToLive: options.timeToLive,
    }),
  );
  const chunkAtom = Atom.family((params: LegacyChapterChunkParams) =>
    chunkSource(params).pipe(
      Atom.map((result) => AsyncResult.map(result, options.toPage)),
    ),
  );
  const chunksAtom = Atom.family(
    (chunks: ReadonlyArray<LegacyChapterChunkParams>) =>
      Atom.make((get) => chunks.map((params) => get(chunkAtom(params)))),
  );
  return { chunkAtom, chunkSource, chunksAtom };
};

export type LegacyChapterChunkReader<TPage, E> = ReturnType<
  typeof makeLegacyChapterChunkReader<TPage, E>
>;

export type LegacyChapterChunkState<TPage> = {
  status: 'idle' | 'pending' | 'error' | 'success';
  /** No data yet: outside the window, or in flight. */
  isPending: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  data: TPage | undefined;
};

const IDLE_CHUNK = {
  status: 'idle',
  isPending: true,
  isFetching: false,
  isError: false,
  error: null,
  data: undefined,
} as const satisfies LegacyChapterChunkState<never>;

const sameChunkState = <TPage>(
  a: LegacyChapterChunkState<TPage>,
  b: LegacyChapterChunkState<TPage>,
) =>
  a.status === b.status &&
  a.isFetching === b.isFetching &&
  a.isError === b.isError &&
  a.error === b.error &&
  a.data === b.data;

const sortMembers = <T extends string | number>(values: Iterable<T>) =>
  [...new Set(values)].toSorted((a, b) => (a < b ? -1 : a > b ? 1 : 0));

export function useDebouncedAnchors<T extends string | number>(
  delayMs = LEGACY_CHAPTER_VISIBILITY_DEBOUNCE_MS,
) {
  const [value, setValue] = useState<ReadonlyArray<T>>([]);
  const latestRef = useRef<ReadonlyArray<T>>([]);
  const pendingRef = useRef<ReadonlyArray<T> | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    },
    [],
  );
  const flush = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (pendingRef.current !== null) {
      setValue(pendingRef.current);
      pendingRef.current = null;
    }
  }, []);
  const update = useCallback(
    (next: Iterable<T>) => {
      const sorted = sortMembers(next);
      if (shallowEqualArrays(sorted, latestRef.current)) return;
      latestRef.current = sorted;
      pendingRef.current = sorted;
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        pendingRef.current = null;
        setValue(sorted);
      }, delayMs);
    },
    [delayMs],
  );
  return { value, update, flush };
}

export type UseLegacyChapterChunksOptions<TPage, E> = {
  audioId: string;
  chapters: ReadonlyArray<{ id: string }>;
  reader: LegacyChapterChunkReader<TPage, E>;
  enabled?: boolean;
  currentChapterIndex: number | undefined;
  /** A playing chapter for this document is still being resolved. The rows on
   * screen are wherever the list mounted, so anchoring on them now would load a
   * window playback is about to move away from. */
  currentChapterPending?: boolean;
  visibleChapterIndices: ReadonlyArray<number>;
  /** Web pins chunk 0 because its SSR loader prefetches it. */
  alwaysFirstChunk: boolean;
  chunkSize?: number;
};

export function useLegacyChapterChunks<TPage, E>({
  audioId,
  chapters,
  reader,
  enabled = true,
  currentChapterIndex,
  currentChapterPending = false,
  visibleChapterIndices,
  alwaysFirstChunk,
  chunkSize,
}: UseLegacyChapterChunksOptions<TPage, E>) {
  const anchorChapterIndices = useMemo(() => {
    const visible = currentChapterPending ? [] : visibleChapterIndices;
    return sortMembers(
      currentChapterIndex === undefined || currentChapterIndex < 0
        ? visible
        : [...visible, currentChapterIndex],
    );
  }, [currentChapterIndex, currentChapterPending, visibleChapterIndices]);
  const chunkParams = useMemo(
    () => legacyChapterChunkParams(audioId, chapters, chunkSize),
    [audioId, chapters, chunkSize],
  );
  const observedChunkIndices = useMemo(
    () =>
      sortMembers(
        enabledLegacyChapterChunks({
          chunkCount: chunkParams.length,
          anchorChapterIndices,
          behind: LEGACY_CHAPTER_CHUNKS_BEHIND,
          ahead: LEGACY_CHAPTER_CHUNKS_AHEAD,
          alwaysFirstChunk,
          ...(chunkSize === undefined ? {} : { chunkSize }),
        }),
      ),
    [alwaysFirstChunk, anchorChapterIndices, chunkParams.length, chunkSize],
  );
  // Every chunk this reader loaded stays mounted until it closes, so
  // scrolling back never refetches and closing releases them all.
  const [retained, setRetained] = useState<{
    readonly audioId: string;
    readonly chunkIndices: ReadonlyArray<number>;
  }>({ audioId, chunkIndices: [] });
  const retainedIndices =
    retained.audioId === audioId ? retained.chunkIndices : [];
  const mountedIndices = enabled
    ? sortMembers([...retainedIndices, ...observedChunkIndices])
    : [];
  if (
    enabled &&
    (retained.audioId !== audioId ||
      !shallowEqualArrays(mountedIndices, retainedIndices))
  )
    setRetained({ audioId, chunkIndices: mountedIndices });
  const mountedParams = mountedIndices.flatMap(
    (chunkIndex) => chunkParams[chunkIndex] ?? [],
  );
  const mountedResults = useAtomValue(reader.chunksAtom(mountedParams));

  const nextStates: Array<LegacyChapterChunkState<TPage>> = Array.from(
    { length: chunkParams.length },
    () => IDLE_CHUNK,
  );
  const observed = new Set(observedChunkIndices);
  mountedIndices.forEach((chunkIndex, position) => {
    const result = mountedResults[position];
    if (!result || !observed.has(chunkIndex)) return;
    const data = AsyncResult.getOrElse(result, () => undefined);
    const failed = AsyncResult.isFailure(result);
    nextStates[chunkIndex] = {
      status:
        data !== undefined && !failed
          ? 'success'
          : failed
            ? 'error'
            : 'pending',
      isPending: data === undefined && !failed,
      isFetching: result.waiting,
      isError: failed,
      error: failed ? result.cause : null,
      data,
    };
  });
  const [shared, setShared] = useState<{
    chunkStates: ReadonlyArray<LegacyChapterChunkState<TPage>>;
    chunkData: ReadonlyArray<TPage | undefined>;
  }>({ chunkStates: [], chunkData: [] });
  const previousStates = shared.chunkStates;
  const chunkStates =
    previousStates.length === nextStates.length &&
    nextStates.every((state, index) => {
      const before = previousStates[index];
      return before !== undefined && sameChunkState(before, state);
    })
      ? previousStates
      : nextStates.map((state, index) => {
          const before = previousStates[index];
          return before !== undefined && sameChunkState(before, state)
            ? before
            : state;
        });
  const nextChunkData = chunkStates.map((state) => state.data);
  const chunkData = shallowEqualArrays(shared.chunkData, nextChunkData)
    ? shared.chunkData
    : nextChunkData;
  if (chunkStates !== shared.chunkStates || chunkData !== shared.chunkData) {
    setShared({ chunkStates, chunkData });
  }

  const registry = use(RegistryContext);
  const retryChunk = useCallback(
    (params: LegacyChapterChunkParams) =>
      registry.refresh(reader.chunkAtom(params)),
    [reader, registry],
  );

  return { chunkParams, chunkStates, chunkData, retryChunk };
}
